import { motion } from "framer-motion";
import { Cpu, Zap, Waves, MessagesSquare } from "lucide-react";

const CHAPTERS = [
  {
    number: "01",
    title: "Intelligente Technik",
    badge: "Smart Automation",
    icon: Cpu,
    content:
      "Automatisierte Filter-, Dosier- und Temperatursysteme — in unserem Haus entwickelt, bequem per Smartphone gesteuert.",
    image: "/images/cp-technik.webp",
  },
  {
    number: "02",
    title: "Integrierte Elektrik",
    badge: "Aus einer Hand",
    icon: Zap,
    content:
      "Pooltechnik und Elektroinstallation aus einer Hand — installiert nach höchsten Sicherheitsstandards.",
    image: "/images/cp-elektrik.jpg",
  },
  {
    number: "03",
    title: "Kristallklare Wasserqualität",
    badge: "Water Balance",
    icon: Waves,
    content:
      "Präzise Wasseraufbereitung — türkis, hautfreundlich, glasklar. Jeden Tag der Saison.",
    image: "/images/cp-wasser.jpg",
  },
  {
    number: "04",
    title: "Persönliche Beratung",
    badge: "1-on-1 Service",
    icon: MessagesSquare,
    content:
      "Von der Planung bis zur Wartung: ein fester Ansprechpartner für Ihr Traumpool-Projekt.",
    image: "/images/cp-beratung.jpg",
  },
];

export default function ManifestoChapters() {
  return (
    <section id="technik" data-testid="manifesto-chapters-section" className="relative py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.32em] font-mono text-cyan-400 font-semibold"
        >
          Unser Manifest
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-4 font-bold tracking-tight leading-tight text-2xl sm:text-4xl lg:text-5xl text-white max-w-3xl"
        >
          Vier Kapitel. <span className="text-cyan-400">Ein Anspruch:</span> Perfektion in jedem Projekt.
        </motion.h2>

        <div className="mt-16 space-y-16 sm:space-y-24">
          {CHAPTERS.map((c, idx) => {
            const Icon = c.icon;
            const reversed = idx % 2 === 1;
            return (
              <motion.article
                key={c.number}
                data-testid={`manifesto-chapter-${c.number}`}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="relative">
                  <span className="chapter-number font-display font-extrabold text-[7rem] sm:text-[10rem] leading-none absolute -top-14 sm:-top-20 -left-2 select-none pointer-events-none">
                    {c.number}
                  </span>
                  <motion.div
                    whileHover={{ scale: 0.985 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="spotlight-img relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,229,255,0.15)]"
                  >
                    <img src={c.image} alt={c.title} className="w-full aspect-[4/3] object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/70 via-transparent to-transparent" />
                  </motion.div>
                </div>
                <div className={reversed ? "lg:pr-6" : "lg:pl-6"}>
                  <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-mono text-cyan-300 bg-cyan-400/10 border border-cyan-400/25 rounded-full px-4 py-1.5">
                    <Icon className="w-3.5 h-3.5" /> {c.badge}
                  </span>
                  <h3 className="mt-5 font-display font-bold text-2xl sm:text-3xl text-white tracking-tight">{c.title}</h3>
                  <p className="mt-4 text-slate-300/90 leading-relaxed text-sm sm:text-base max-w-lg">{c.content}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
